import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
// import Logo from '../static/logo.png';
import { cn } from '../cn';
import { MdBarChart, MdAdd, MdTableChart, MdDownload, MdDashboard, MdScience, MdTrendingUp, MdMonitor } from 'react-icons/md';
import DarkModeSwitcher from '../components/DarkModeSwitcher';
import logo from '../static/logo.png';

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (arg: boolean) => void;
}

const Sidebar = ({ sidebarOpen, setSidebarOpen }: SidebarProps) => {
  const location = useLocation();
  const { pathname } = location;

  const trigger = useRef<any>(null);
  const sidebar = useRef<any>(null);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!sidebar.current || !trigger.current) return;
      if (!sidebarOpen || sidebar.current.contains(target) || trigger.current.contains(target)) return;
      setSidebarOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  return (
    <aside
      ref={sidebar}
      className={cn(
        'absolute left-0 top-0 z-9999 flex h-screen w-72.5 flex-col overflow-y-hidden bg-gray-800 duration-300 ease-linear lg:static lg:translate-x-0',
        {
          'translate-x-0': sidebarOpen,
          '-translate-x-full': !sidebarOpen,
        }
      )}
    >
      {/* <!-- SIDEBAR HEADER --> */}
                    <div className='flex items-center justify-between gap-2 px-6 py-5.5 lg:py-6.5'>
                <NavLink to='/'>
                  <div className="flex items-center">
                    <img className="h-8 w-8" src={logo} alt="Pricing Table AI" />
                    <span className="ml-2 text-sm font-semibold leading-6 text-white">PricingTable AI</span>
                  </div>
                </NavLink>

        <button
          ref={trigger}
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-controls='sidebar'
          aria-expanded={sidebarOpen}
          className='block lg:hidden'
        >
          <svg
            className='fill-current'
            width='20'
            height='18'
            viewBox='0 0 20 18'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M19 8.175H2.98748L9.36248 1.6875C9.69998 1.35 9.69998 0.825 9.36248 0.4875C9.02498 0.15 8.49998 0.15 8.16248 0.4875L0.399976 8.3625C0.0624756 8.7 0.0624756 9.225 0.399976 9.5625L8.16248 17.4375C8.31248 17.5875 8.53748 17.7 8.76248 17.7C8.98748 17.7 9.17498 17.625 9.36248 17.475C9.69998 17.1375 9.69998 16.6125 9.36248 16.275L3.02498 9.8625H19C19.45 9.8625 19.825 9.4875 19.825 9.0375C19.825 8.55 19.45 8.175 19 8.175Z'
              fill=''
            />
          </svg>
        </button>
      </div>
      {/* <!-- SIDEBAR HEADER --> */}

      <div className='no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear'>
        {/* <!-- Sidebar Menu --> */}
        <nav className='mt-5 py-4 px-4 lg:mt-9 lg:px-6'>
          {/* <!-- Menu Group --> */}
          <div>
            <h3 className='mb-4 ml-4 text-sm font-semibold text-bodydark2'>MENU</h3>

            <ul className='mb-6 flex flex-col gap-1.5'>
              {/* <!-- Menu Item Dashboard --> */}
              <li>
                <NavLink
                  to='/dashboard'
                  className={({ isActive }) =>
                    cn(
                      'group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-white duration-300 ease-in-out hover:bg-gray-600',
                      { 'bg-gray-600': isActive }
                    )
                  }
                >
                  <MdDashboard className='h-5 w-5' />
                  Dashboard
                </NavLink>
              </li>

              {/* <!-- Menu Item Analytics --> */}
              <li>
                <NavLink
                  to='/analytics'
                  className={({ isActive }) =>
                    cn(
                      'group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-white duration-300 ease-in-out hover:bg-gray-600',
                      { 'bg-gray-600': isActive }
                    )
                  }
                >
                  <MdBarChart className='h-5 w-5' />
                  Analytics
                </NavLink>
              </li>

              {/* <!-- Menu Item Upload Pricing Table --> */}
              <li>
                <NavLink
                  to='/upload-pricing-table'
                  className={({ isActive }) =>
                    cn(
                      'group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-white duration-300 ease-in-out hover:bg-gray-600',
                      { 'bg-gray-600': isActive }
                    )
                  }
                >
                  <MdAdd className='h-5 w-5' />
                  Upload Pricing Table
                </NavLink>
              </li>

              {/* <!-- Menu Item View Pricing Tables --> */}
              <li>
                <NavLink
                  to='/pricing-tables'
                  className={({ isActive }) =>
                    cn(
                      'group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-white duration-300 ease-in-out hover:bg-gray-600',
                      { 'bg-gray-600': isActive }
                    )
                  }
                >
                  <MdTableChart className='h-5 w-5' />
                  View Pricing Tables
                </NavLink>
              </li>

              {/* <!-- Menu Item Install --> */}
              <li>
                <NavLink
                  to='/install'
                  className={({ isActive }) =>
                    cn(
                      'group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-white duration-300 ease-in-out hover:bg-gray-600',
                      { 'bg-gray-600': isActive }
                    )
                  }
                >
                  <MdDownload className='h-5 w-5' />
                  Install
                </NavLink>
              </li>

              {/* <!-- Menu Item AI Pricing Findings --> */}
              <li>
                <NavLink
                  to='/findings'
                  className={({ isActive }) =>
                    cn(
                      'group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-white duration-300 ease-in-out hover:bg-gray-600',
                      { 'bg-gray-600': isActive }
                    )
                  }
                >
                  <MdScience className='h-5 w-5' />
                  AI Pricing Findings
                </NavLink>
              </li>

              {/* <!-- Menu Item Optimization Dashboard --> */}
              <li>
                <NavLink
                  to='/optimization/dashboard'
                  className={({ isActive }) =>
                    cn(
                      'group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-white duration-300 ease-in-out hover:bg-gray-600',
                      { 'bg-gray-600': isActive }
                    )
                  }
                >
                  <MdTrendingUp className='h-5 w-5' />
                  Optimization Dashboard
                </NavLink>
              </li>

              {/* <!-- Menu Item System Monitoring --> */}
              <li>
                <NavLink
                  to='/monitoring/dashboard'
                  className={({ isActive }) =>
                    cn(
                      'group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-white duration-300 ease-in-out hover:bg-gray-600',
                      { 'bg-gray-600': isActive }
                    )
                  }
                >
                  <MdMonitor className='h-5 w-5' />
                  System Monitoring
                </NavLink>
              </li>
            </ul>
          </div>
        </nav>
        {/* <!-- Sidebar Menu --> */}
      </div>
    </aside>
  );
};

export default Sidebar;
